import { defineStore } from 'pinia';
import {
  fetchAuthSession as AmplifyFetchAuthSession,
  fetchUserAttributes as AmplifyFetchUserAttributes,
  updateUserAttributes as AmplifyUpdateUserAttributes
} from 'aws-amplify/auth';

import { USER_RIGHTS } from '@base-shared/utils/constants';
import type { AppError } from '@base-shared/utils/error-handler';
import { handleRequest } from '@base-shared/utils/request-handler';
import { useGroupApi, useUserApi } from '@base-shared/composables/use-api';
import type { paths } from '@base-shared/types/group.openapi';

type GroupUserAccountsOperation = paths['/group/accounts']['get'];
type GroupUserAccountsResponse = GroupUserAccountsOperation['responses'][200]['content']['application/json'];
type GroupUserGetOperation = paths['/group/users/{userid}']['get'];
type GroupUserGetResponse = GroupUserGetOperation['responses'][200]['content']['application/json'];
type AccountProductsPatchOperation = paths['/group/products']['patch'];
type AccountProductsPatchRequest = AccountProductsPatchOperation['requestBody']['content']['application/json'];
type UserMePatch = paths['/user/me']['patch'];
type UserMePatchRequest = UserMePatch['requestBody']['content']['application/json'];

export type Account = GroupUserAccountsResponse['accounts'][number];
export type User = GroupUserGetResponse & {
  password?: string;
  usedExternalIdP?: boolean;
};
type Products = NonNullable<AccountProductsPatchRequest['add'] | AccountProductsPatchRequest['remove']>;

interface UserState {
  isRequestProcessing: boolean;
  accounts: Account[];
  enabledProducts: Array<string>;
  currentUser: User | null;
  currentUserGroupId: string | null;
  currentUserRights: string[];
  isLoadingCurrentUser: boolean;
  requestError: null | AppError;
  requestErrorMessage: null | string;
}

export const useUserStore = defineStore('user', {
  state: (): UserState => ({
    isRequestProcessing: false,
    accounts: [],
    enabledProducts: [],
    currentUser: null,
    currentUserGroupId: null,
    currentUserRights: [],
    isLoadingCurrentUser: false,
    requestError: null,
    requestErrorMessage: null
  }),
  getters: {
    errorMessage: (state) => state.requestErrorMessage ?? state.requestError?.translatedMessage,
    currentSelectedAccountId: (state) => state.currentUserGroupId,
    currentSelectedAccount: (state) => {
      const accounts = state.accounts;
      const currentUserGroupId = state.currentUserGroupId;

      if (!currentUserGroupId || !accounts?.length) {
        return null;
      }

      // Get currentUserAccount by comparing current user's groupId from list of all accounts fetched
      return accounts.find((account) => account.groupId === currentUserGroupId) ?? null;
    },
    currentSelectedAccountName() {
      return this.currentSelectedAccount?.name ?? '';
    },
    isGlobalAdmin: (state) => state.currentUserRights?.includes?.(USER_RIGHTS.GLOBAL_ADMIN) ?? false,
    isSubscriptionManager: (state) => state.currentUserRights?.includes?.(USER_RIGHTS.SUBSCRIPTION_MANAGER) ?? false,
    hasDevelopmentRights: (state) => state.currentUserRights?.includes?.(USER_RIGHTS.DEVELOPMENT) ?? false,
    isCurrentUser: (state) => (user: User) => user.userId === state.currentUser?.userId
  },
  actions: {
    setError(requestError: AppError | null, errorMessage?: string) {
      this.requestError = requestError;
      this.requestErrorMessage = errorMessage ?? requestError?.translatedMessage ?? null;
    },
    setLoading(isRequestProcessing: boolean) {
      this.isRequestProcessing = isRequestProcessing;

      if (isRequestProcessing) {
        this.setError(null);
      }
    },
    /**
     * Fetches the current user's group ID from the session.
     *
     * Get Cognito Group Id from user session. If no `custom:group_id`
     * is found, use the `preferred username`, or if not present, use
     * `sub`. For most users `preferred_username` and sub are always
     * equal, but that is no longer the case when users are moved from
     * one region to another, so it is important to have the fallbacks
     * in the proper order.
     */
    async getCurrentUserGroupId() {
      const userSession = await AmplifyFetchAuthSession();
      const sessionIdToken = userSession?.tokens?.idToken?.payload;
      if (!sessionIdToken) {
        this.currentUserGroupId = null;
        return this.currentUserGroupId;
      }

      const sessionGroupId = sessionIdToken['custom:group_id']
        ?? sessionIdToken['preferred_username']
        ?? sessionIdToken['sub'];

      this.currentUserGroupId = typeof sessionGroupId === 'string' && sessionGroupId.trim()?.length
        ? sessionGroupId
        : null;

      return this.currentUserGroupId;
    },
    async acceptTOS() {
      const response = { success: false };
      try {
        this.isRequestProcessing = true;
        await useGroupApi().acceptToS();

        // Force Refresh to get updated tos_accept_date in storage
        await AmplifyFetchAuthSession({ forceRefresh: true });

        response.success = true;
      } catch (error) {
        console.error('Unexpected Error: ', error);
      }

      this.isRequestProcessing = false;
      return response;
    },
    async showTermsOfService() {
      const CONFIG = useRuntimeConfig();
      const session = await AmplifyFetchAuthSession();

      // If no session return false
      if (!session.tokens) {
        return false;
      }

      // Allow redirect for users that already accepted ToS
      // As long as acceptance date is after the latest TOS date of update
      if (session.tokens?.idToken?.payload?.['custom:tos_accept_date']) {
        const latestTOS = (CONFIG.public.latestTermsOfServiceDate ?? '') as string;

        if (!latestTOS) {
          console.error('Latest TOS date not set in config');
          return true;
        }

        const userAcceptanceDate = session.tokens?.idToken?.payload?.['custom:tos_accept_date'];

        if (userAcceptanceDate < latestTOS) {
          return true;
        }
      } else if (!session.tokens?.idToken?.payload?.['custom:tos_accept_date']) {
        // Return to ToS page if user did not accept ToS yet
        return true;
      }
      return false;
    },
    async enableProducts(products: Products) {
      const { success, error, response } = await handleRequest(async () => {
        const attributes = await AmplifyFetchUserAttributes();
        await useGroupApi().patchModifyAccountProducts({ add: products });
        const result = await useUserApi().patchModifyUserProducts(attributes.preferred_username, { add: products });
        await AmplifyFetchAuthSession({ forceRefresh: true });

        return { entitlements: result.data.products };
      });

      if (success) {
        this.enabledProducts = response?.entitlements ?? [];
      }

      return { success, error };
    },
    async getCurrentUserDetails() {
      this.isLoadingCurrentUser = true;
      const { success, error, response } = await handleRequest(async () => {
        // Get the current user group ID from local storage
        await this.getCurrentUserGroupId();

        const result = await useUserApi().getUser();
        console.log('user#getCurrentUserDetails', result);

        const session = await AmplifyFetchAuthSession();
        const sessionIdToken = session?.tokens?.idToken?.payload;

        // Get the user's Cognito groups from the session
        // id token to be stored as the currentUserRights
        const cognitoGroups = sessionIdToken?.['cognito:groups'] ?? [];
        const sessionRights = Array.isArray(cognitoGroups) && cognitoGroups.every((i) => typeof i === 'string')
          ? cognitoGroups as string[]
          : [];

        // Get the user's entitlements from the session
        // id token to be stored as the enabledProducts
        const sessionEntitlements = JSON.parse(sessionIdToken?.['entitlements']?.toString() ?? '[]');

        return {
          currentUser: {
            ...result?.data,
            usedExternalIdP: !!sessionIdToken?.at_hash
          },
          entitlements: sessionEntitlements,
          rights: sessionRights
        };
      });

      this.isLoadingCurrentUser = false;

      if (success) {
        this.currentUser = response?.currentUser ?? null;
        this.currentUserRights = response?.rights ?? [];
        this.enabledProducts = response?.entitlements;
      }

      return { success, error, response };
    },
    async handleFetchCurrentUserDetails() {
      this.setLoading(true);

      const { success, error, response } = await this.getCurrentUserDetails();

      if (!success && error) {
        this.setError(error ?? null, error?.translatedMessage ?? 'errors.unexpectedError');
      }

      this.setLoading(false);
      return { success, error, response };
    },
    async updateCognitoUserAttributes(user: { firstName: string; lastName: string }) {
      this.setLoading(true);

      const { success, error } = await handleRequest(async () => {
        const result = await AmplifyUpdateUserAttributes({
          userAttributes: {
            given_name: user.firstName,
            family_name: user.lastName
          }
        });
        console.log('user#updateUserDetails', result);
      });

      // TODO: Add check if same user
      if (!this.currentUser) {
        const { error } = await this.getCurrentUserDetails();

        throw (error);
      } else {
        this.currentUser.firstName = user.firstName;
        this.currentUser.lastName = user.lastName;
      }

      if (!success) {
        this.setError(error ?? null, error?.translatedMessage ?? 'errors.unexpectedError');
      }

      this.setLoading(false);
    },
    async updateUserDetails(payload: UserMePatchRequest) {
      payload.email = payload.email?.toLowerCase?.() ?? payload.email;
      this.setLoading(true);

      const { success, error } = await handleRequest(async () => {
        await useUserApi().updateUser(payload);
      });

      if (success) {
        // Set the updated user details in the store
        // if available
        if (this.currentUser) {
          this.currentUser.email = payload.email ?? '';
        }
      } else {
        this.setError(error ?? null, error?.translatedMessage ?? 'errors.unexpectedError');
      }

      this.setLoading(false);
    },
    async getEnabledProducts() {
      const { success, error, response } = await handleRequest(async () => {
        const session = await AmplifyFetchAuthSession();
        const entitlements = JSON.parse(session?.tokens?.idToken?.payload?.entitlements?.toString() ?? '[]');

        return { entitlements };
      });

      if (success) {
        this.enabledProducts = response?.entitlements;
      }

      return { success, error, response };
    },
    async getAccounts() {
      const { success, error, response } = await handleRequest(async () => {
        const accountListResult = await useGroupApi().getAccounts();
        return { accounts: accountListResult.data?.accounts ?? [] };
      });

      if (success) {
        this.accounts = response?.accounts ?? [];
      }

      return { success, error, response };
    },
    async deleteCurrentUser() {
      this.setLoading(true);

      const { success, error } = await handleRequest(async () => {
        return await useUserApi().deleteUser();
      });

      this.setLoading(false);

      return { success, error };
    }
  }
});
